// Export modal: PNG / GIF / WebM / MP4 / Copy clipboard.

import { state } from '@/core/state';
import type { AppState } from '@/core/types';
import { el, on } from '@/lib/dom';

// All export modules are lazy-loaded so gifenc / MediaRecorder code only ships
// when the user actually exports.
const lazyPng       = () => import('@/export/png');
const lazyClipboard = () => import('@/export/clipboard');
const lazyGif       = () => import('@/export/gif');
const lazyWebm      = () => import('@/export/webm');
const lazyMp4       = () => import('@/export/mp4');

let webmSupportedCache: boolean | null = null;
function isWebmSupported(): boolean {
  if (webmSupportedCache !== null) return webmSupportedCache;
  if (typeof MediaRecorder === 'undefined') return (webmSupportedCache = false);
  webmSupportedCache =
    MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ||
    MediaRecorder.isTypeSupported('video/webm;codecs=vp8') ||
    MediaRecorder.isTypeSupported('video/webm');
  return webmSupportedCache;
}

let mp4SupportedCache: boolean | null = null;
function isMp4Supported(): boolean {
  if (mp4SupportedCache !== null) return mp4SupportedCache;
  if (typeof MediaRecorder === 'undefined') return (mp4SupportedCache = false);
  mp4SupportedCache =
    MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.640033') ||
    MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.4D4028') ||
    MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.42E01E') ||
    MediaRecorder.isTypeSupported('video/mp4');
  return mp4SupportedCache;
}

type Mode = 'png' | 'clipboard' | 'gif' | 'webm' | 'mp4';

type ResPreset = 'native' | '1080p' | '1440p' | '4k' | '8k';

const RES_PRESETS: { id: ResPreset; label: string; longSide: number | null }[] = [
  { id: 'native', label: 'Native (current)', longSide: null },
  { id: '1080p',  label: 'HD 1080p',         longSide: 1920 },
  { id: '1440p',  label: '2K 1440p',         longSide: 2560 },
  { id: '4k',     label: '4K UHD',           longSide: 3840 },
  { id: '8k',     label: '8K UHD',           longSide: 7680 },
];

function resolveResolution(s: AppState, preset: ResPreset): { w: number; h: number } {
  const def = RES_PRESETS.find((p) => p.id === preset);
  if (!def || def.longSide === null) return { w: s.resolution.w, h: s.resolution.h };
  const aspect = s.resolution.w / s.resolution.h;
  let w: number, h: number;
  if (aspect >= 1) { w = def.longSide; h = Math.round(def.longSide / aspect); }
  else             { h = def.longSide; w = Math.round(def.longSide * aspect); }
  // H.264 requires even dimensions; snap up to keep MP4 happy.
  if (w % 2) w += 1;
  if (h % 2) h += 1;
  return { w, h };
}

function applyResolution(s: AppState, preset: ResPreset): AppState {
  const r = resolveResolution(s, preset);
  if (r.w === s.resolution.w && r.h === s.resolution.h) return s;
  // Pixel-based dotgrad params (cell, gap, spread) need to scale with the
  // canvas — otherwise at 4K/8K the dot grid becomes microscopic and the
  // black gaps blend with the colored dots, dimming the image. Scaling these
  // keeps the visible pattern density identical to the live preview.
  const scale = r.w / s.resolution.w;
  const layers = s.layers.map((layer) => {
    if (layer.kind !== 'dotgrad') return layer;
    const params = { ...layer.params };
    const cell = (params['cell'] as number | undefined) ?? 8;
    const gap = (params['gap'] as number | undefined) ?? 1;
    const spread = (params['spread'] as number | undefined) ?? 800;
    params['cell'] = Math.max(2, Math.round(cell * scale));
    params['gap'] = Math.max(0, Math.round(gap * scale));
    params['spread'] = Math.max(2, Math.round(spread * scale));
    return { ...layer, params };
  });
  return { ...s, resolution: r, layers };
}

// Frame ceiling — 60 sec at 60 fps. Long enough for the user's "1 minute" use
// case without unbounded growth.
const MAX_FRAMES = 3600;
const MAX_DURATION_SEC = 600;

export function openExportDialog(): void {
  let mode: Mode = 'png';
  let resPreset: ResPreset = 'native';
  let fps = 30;
  let duration = 2;            // seconds — primary input
  let frames = duration * fps; // derived; export uses this
  let busy = false;

  const dim = el('div', { class: 'modal-backdrop' });
  const dialog = el('div', { class: 'modal modal-export', role: 'dialog', ariaLabel: 'Export' });

  const head = el('div', { class: 'modal-head' }, [
    el('div', { class: 'modal-title' }, ['Export']),
    el('div', { class: 'modal-subtitle' }, [
      `${state.peek().resolution.w}×${state.peek().resolution.h}  ·  exports at native pixel size`,
    ]),
  ]);

  // Format tabs
  const tabs = el('div', { class: 'export-tabs', role: 'tablist' });
  const tabBtns: Record<Mode, HTMLButtonElement> = {
    png: tabBtn('PNG'),
    clipboard: tabBtn('Copy'),
    gif: tabBtn('GIF'),
    webm: tabBtn('WebM'),
    mp4: tabBtn('MP4'),
  };
  if (!isWebmSupported()) {
    tabBtns.webm.setAttribute('disabled', '');
    tabBtns.webm.title = 'WebM not supported in this browser';
  }
  if (!isMp4Supported()) {
    tabBtns.mp4.setAttribute('disabled', '');
    tabBtns.mp4.title = 'MP4 (H.264) recording not supported in this browser';
  }
  for (const [m, b] of Object.entries(tabBtns)) {
    on(b, 'click', () => { mode = m as Mode; updateTabs(); });
    tabs.appendChild(b);
  }

  function updateTabs(): void {
    for (const [m, b] of Object.entries(tabBtns)) {
      b.classList.toggle('active', m === mode);
    }
    animBox.style.display = (mode === 'gif' || mode === 'webm' || mode === 'mp4') ? 'flex' : 'none';
    refreshSummary();
  }

  // Resolution preset — applies to images and video alike.
  const resIn = el('select', { class: 'sd-input mono' }) as HTMLSelectElement;
  for (const p of RES_PRESETS) {
    const opt = el('option', { value: p.id }) as HTMLOptionElement;
    opt.textContent = p.label;
    resIn.appendChild(opt);
  }
  resIn.value = resPreset;
  on(resIn, 'change', () => { resPreset = resIn.value as ResPreset; refreshSummary(); });

  // Animation options. Three live-synced inputs: duration (sec), fps, frames.
  // Editing any one updates the others so the user can think in whichever
  // unit they prefer ("I want 60 seconds at 30 fps" or "exactly 120 frames").
  const durationIn = el('input', { type: 'number', value: String(duration) }) as HTMLInputElement;
  durationIn.min = '0.1'; durationIn.max = String(MAX_DURATION_SEC); durationIn.step = '0.1';
  durationIn.className = 'sd-input mono';

  const fpsIn = el('input', { type: 'number', value: String(fps) }) as HTMLInputElement;
  fpsIn.min = '1'; fpsIn.max = '60'; fpsIn.step = '1';
  fpsIn.className = 'sd-input mono';

  const framesIn = el('input', { type: 'number', value: String(frames) }) as HTMLInputElement;
  framesIn.min = '1'; framesIn.max = String(MAX_FRAMES); framesIn.step = '1';
  framesIn.className = 'sd-input mono';

  const summary = el('div', { class: 'export-summary mono' });

  function clampDuration(v: number): number { return Math.max(0.1, Math.min(MAX_DURATION_SEC, v)); }
  function clampFps(v: number): number { return Math.max(1, Math.min(60, Math.floor(v))); }
  function clampFrames(v: number): number { return Math.max(1, Math.min(MAX_FRAMES, Math.floor(v))); }

  let syncing = false;
  function refreshSummary(): void {
    const r = resolveResolution(state.peek(), resPreset);
    const resStr = `${r.w}×${r.h}`;
    if (mode === 'png' || mode === 'clipboard') {
      summary.textContent = resStr;
    } else {
      const wallSec = (frames / fps).toFixed(1);
      summary.textContent = `${resStr} · ${frames} frames · ≈ ${wallSec}s playback`;
    }
  }
  function syncFromDuration(): void {
    if (syncing) return;
    syncing = true;
    const desired = Math.round(duration * fps);
    frames = clampFrames(desired);
    if (frames !== desired) {
      // Hit the frames cap — pull duration back so the displayed value is truthful.
      duration = clampDuration(frames / fps);
      durationIn.value = duration.toFixed(1);
    }
    framesIn.value = String(frames);
    refreshSummary();
    syncing = false;
  }
  function syncFromFps(): void {
    if (syncing) return;
    syncing = true;
    const desired = Math.round(duration * fps);
    frames = clampFrames(desired);
    if (frames !== desired) {
      duration = clampDuration(frames / fps);
      durationIn.value = duration.toFixed(1);
    }
    framesIn.value = String(frames);
    refreshSummary();
    syncing = false;
  }
  function syncFromFrames(): void {
    if (syncing) return;
    syncing = true;
    duration = clampDuration(frames / fps);
    durationIn.value = duration.toFixed(1);
    refreshSummary();
    syncing = false;
  }

  on(durationIn, 'change', () => {
    duration = clampDuration(parseFloat(durationIn.value) || duration);
    durationIn.value = duration.toFixed(1);
    syncFromDuration();
  });
  on(fpsIn, 'change', () => {
    fps = clampFps(parseInt(fpsIn.value, 10) || fps);
    fpsIn.value = String(fps);
    syncFromFps();
  });
  on(framesIn, 'change', () => {
    frames = clampFrames(parseInt(framesIn.value, 10) || frames);
    framesIn.value = String(frames);
    syncFromFrames();
  });

  refreshSummary();

  const resBox = el('div', { class: 'export-anim' }, [
    fieldRow('Resolution', resIn),
  ]);

  const animBox = el('div', { class: 'export-anim' }, [
    fieldRow('Duration (sec)', durationIn),
    fieldRow('FPS', fpsIn),
    fieldRow('Frames', framesIn),
  ]);

  const summaryBox = el('div', { class: 'export-anim' }, [summary]);

  const status = el('div', { class: 'export-status mono' });

  const goBtn = el('button', { class: 'btn modal-btn modal-btn-primary' }, ['Export']);
  on(goBtn, 'click', () => { void run(); });
  const closeBtn = el('button', { class: 'btn modal-btn' }, ['Close']);
  on(closeBtn, 'click', () => close());
  const actions = el('div', { class: 'modal-actions' }, [status, goBtn, closeBtn]);

  dialog.append(head, tabs, resBox, animBox, summaryBox, actions);
  dim.appendChild(dialog);
  document.body.appendChild(dim);
  updateTabs();

  function close(): void {
    dim.remove();
    offKey();
    offClick();
  }

  const offKey = on(window, 'keydown', (ev) => { if (ev.key === 'Escape' && !busy) close(); });
  const offClick = on(dim, 'click', (ev) => { if (ev.target === dim && !busy) close(); });

  async function run(): Promise<void> {
    if (busy) return;
    busy = true;
    goBtn.setAttribute('disabled', '');
    status.textContent = 'working…';
    try {
      const s = applyResolution(state.peek(), resPreset);
      if (mode === 'png') {
        await (await lazyPng()).exportPng(s);
        status.textContent = 'downloaded';
      } else if (mode === 'clipboard') {
        const ok = await (await lazyClipboard()).copyPngToClipboard(s);
        status.textContent = ok ? 'copied to clipboard' : 'clipboard unavailable';
      } else if (mode === 'gif') {
        await (await lazyGif()).downloadGif(s, {
          frames, fps,
          onProgress: (d, t) => { status.textContent = `${d}/${t}`; },
        });
        status.textContent = 'downloaded';
      } else if (mode === 'webm') {
        await (await lazyWebm()).downloadWebm(s, {
          frames, fps,
          onProgress: (d, t) => { status.textContent = `${d}/${t}`; },
        });
        status.textContent = 'downloaded';
      } else if (mode === 'mp4') {
        await (await lazyMp4()).downloadMp4(s, {
          frames, fps,
          onProgress: (d, t) => { status.textContent = `${d}/${t}`; },
        });
        status.textContent = 'downloaded';
      }
    } catch (err) {
      console.error(err);
      status.textContent = 'failed — see console';
    } finally {
      busy = false;
      goBtn.removeAttribute('disabled');
    }
  }
}

function tabBtn(label: string): HTMLButtonElement {
  const b = el('button', { class: 'export-tab', role: 'tab' }, [label]);
  return b as HTMLButtonElement;
}

function fieldRow(label: string, input: HTMLElement): HTMLElement {
  return el('label', { class: 'export-field' }, [
    el('span', { class: 'sl-label' }, [label]),
    input,
  ]);
}
